// Reporting, markup, delivery and spillover — the analytics reads.
//
// Sixteen resources of one shape: normalise the date range, GET a path with the
// normalised query as a querystring, unwrap `.rows`. `reportingRead` is that
// shape written once. It is not a second factory — it is a call to `defineQuery`
// with two fields filled in, and everything it produces is an ordinary resource.
//
// THE NORMALISATION IS INSIDE THE RESOURCE, ON PURPOSE
// ----------------------------------------------------
// `reportingApiQuery` folds the UI's `from`/`to` into the API's
// `range: 'custom'` + `date_from`/`date_to` contract. Each hook used to call it
// once and use the result for both the key and the URL, which was correct but
// left it possible for a caller — a route loader, say — to build the key from a
// raw `{ from, to }` and open a second cache slot for the same data. Doing it in
// `keyOf` AND in `request` makes that unrepresentable: there is no way to reach
// the slot except through the normalisation.

import { api } from '@/api/client';
import { ApiError } from '@/api/errors';
import { defineQuery } from '@/api/resource';
import type { DataDomain } from '@/api/resource';
import { buildQs } from '@/api/query-string';
import {
  CAMPAIGN_OVERVIEW_DOMAINS,
  CANONICAL_REPORT_DOMAINS,
  DELIVERY_COMPAT_DOMAINS,
  MARKUP_COMPAT_DOMAINS,
  SPILLOVER_COMPAT_DOMAINS,
} from '@/api/types.gen';
import { fmtDateInput } from '@/lib/datetime';
import type {
  CampaignOverviewRequest,
  CampaignOverviewResponse,
  DeliveryPlacementsResponse,
  MarkupByCampaignResponse,
  MarkupByChannelResponse,
  MarkupByProductionResponse,
  MarkupSummaryResponse,
  ReportByAudienceResponse,
  ReportByAudienceTypeResponse,
  ReportByCampaignResponse,
  ReportByChannelResponse,
  ReportByCreativeResponse,
  ReportByProductionResponse,
  ReportQueryRequest,
  ReportQueryResponse,
  ReportSummaryResponse,
  SpilloverFlowsResponse,
  SpilloverSummaryResponse,
  SpilloverTopHaloResponse,
} from '@/api/types';

export interface ReportDateQuery {
  /** Friendly UI inputs; normalized to the API's custom-range contract. */
  from?: string;
  to?: string;
  range?: string;
  date_from?: string;
  date_to?: string;
}

export interface ReportingQuery extends ReportDateQuery {
  production_id?: string;
  campaign_id?: string;
  channel?: string;
  audience_id?: string;
  audience_revision?: number;
  audience_type?: string;
  creative_id?: string;
}

export interface DeliveryQuery extends ReportDateQuery {
  tab?: 'overview' | 'campaign' | 'production' | 'channel' | 'audience' | 'audience_type';
  scope_id?: string;
  audience_revision?: number;
  channel?: string;
}

export interface MarkupQuery extends ReportDateQuery {
  campaign_id?: string;
  production_id?: string;
  channel?: string;
}

export interface SpilloverQuery extends ReportDateQuery {
  production_id?: string;
  campaign_id?: string;
  channel?: string;
  audience_id?: string;
  audience_revision?: number;
  audience_type?: string;
  /** Top-halo only: caps the returned rows. */
  limit?: number;
}

type NormalizedReportQuery<T extends ReportDateQuery> = Omit<T, 'from' | 'to'>;

/** Fold the UI's `from`/`to` into the API's custom-range contract. */
export function reportingApiQuery<T extends ReportDateQuery>(q: T): NormalizedReportQuery<T> {
  const { from, to, ...apiQuery } = q;
  if (from !== undefined || to !== undefined) {
    return {
      ...apiQuery,
      range: 'custom',
      date_from: from === undefined ? undefined : fmtDateInput(from),
      date_to: to === undefined ? undefined : fmtDateInput(to),
    } as NormalizedReportQuery<T>;
  }
  return apiQuery as NormalizedReportQuery<T>;
}

/** One `defineQuery` call with the two reporting-shaped fields filled in. */
function reportingRead<Q extends ReportDateQuery, TRaw, TData = TRaw>(
  name: string,
  path: string,
  domains: readonly DataDomain[],
  unwrap?: (raw: TRaw) => TData,
) {
  return defineQuery({
    namespace: 'reporting' as const,
    name,
    domains,
    keyOf: (q: Q) => [reportingApiQuery(q)],
    request: (q: Q, signal: AbortSignal) =>
      api.get<TRaw>(`${path}${buildQs(reportingApiQuery(q))}`, { signal }),
    unwrap,
  });
}

// The dependency lists are generated from the Rust constants the API
// stamps into each response, so a resource here invalidates on exactly the
// domains the server says its route read. They used to be re-typed by hand in
// this file, and the spillover copy omitted the `creatives` join the canonical
// query makes for its labels. The local names are kept so the declarations
// below read the same as before.
const REPORTING_DOMAINS = CANONICAL_REPORT_DOMAINS;
const DELIVERY_DOMAINS = DELIVERY_COMPAT_DOMAINS;
const MARKUP_DOMAINS = MARKUP_COMPAT_DOMAINS;
const SPILLOVER_DOMAINS = SPILLOVER_COMPAT_DOMAINS;

export const reportingSummary = reportingRead<ReportingQuery, ReportSummaryResponse>(
  'reporting.summary', '/reporting/summary', REPORTING_DOMAINS);

export const reportingByCampaign = reportingRead<ReportingQuery, ReportByCampaignResponse, ReportByCampaignResponse['rows']>(
  'reporting.byCampaign', '/reporting/by-campaign', REPORTING_DOMAINS, (r) => r.rows);

export const reportingByProduction = reportingRead<ReportingQuery, ReportByProductionResponse, ReportByProductionResponse['rows']>(
  'reporting.byProduction', '/reporting/by-production', REPORTING_DOMAINS, (r) => r.rows);

export const reportingByChannel = reportingRead<ReportingQuery, ReportByChannelResponse, ReportByChannelResponse['rows']>(
  'reporting.byChannel', '/reporting/by-channel', REPORTING_DOMAINS, (r) => r.rows);

export const reportingByAudience = reportingRead<ReportingQuery, ReportByAudienceResponse, ReportByAudienceResponse['rows']>(
  'reporting.byAudience', '/reporting/by-audience', REPORTING_DOMAINS, (r) => r.rows);

export const reportingByAudienceType = reportingRead<ReportingQuery, ReportByAudienceTypeResponse, ReportByAudienceTypeResponse['rows']>(
  'reporting.byAudienceType', '/reporting/by-audience-type', REPORTING_DOMAINS, (r) => r.rows);

export const reportingByCreative = reportingRead<ReportingQuery, ReportByCreativeResponse, ReportByCreativeResponse['rows']>(
  'reporting.byCreative', '/reporting/by-creative', REPORTING_DOMAINS, (r) => r.rows);

export const markupSummary = reportingRead<MarkupQuery, MarkupSummaryResponse>(
  'markup.summary', '/markup/summary', MARKUP_DOMAINS);

export const markupByCampaign = reportingRead<MarkupQuery, MarkupByCampaignResponse, MarkupByCampaignResponse['rows']>(
  'markup.byCampaign', '/markup/by-campaign', MARKUP_DOMAINS, (r) => r.rows);

export const markupByChannel = reportingRead<MarkupQuery, MarkupByChannelResponse, MarkupByChannelResponse['rows']>(
  'markup.byChannel', '/markup/by-channel', MARKUP_DOMAINS, (r) => r.rows);

export const markupByProduction = reportingRead<MarkupQuery, MarkupByProductionResponse, MarkupByProductionResponse['rows']>(
  'markup.byProduction', '/markup/by-production', MARKUP_DOMAINS, (r) => r.rows);

export const deliveryPlacements = reportingRead<DeliveryQuery, DeliveryPlacementsResponse, DeliveryPlacementsResponse['rows']>(
  'delivery.placements', '/delivery/placements', DELIVERY_DOMAINS, (r) => r.rows);

export const spilloverSummary = reportingRead<SpilloverQuery, SpilloverSummaryResponse>(
  'spillover.summary', '/spillover/summary', SPILLOVER_DOMAINS);

export const spilloverFlows = reportingRead<SpilloverQuery, SpilloverFlowsResponse, SpilloverFlowsResponse['rows']>(
  'spillover.flows', '/spillover/flows', SPILLOVER_DOMAINS, (r) => r.rows);

export const spilloverTopHalo = reportingRead<SpilloverQuery, SpilloverTopHaloResponse, SpilloverTopHaloResponse['rows']>(
  'spillover.topHalo', '/spillover/top-halo', SPILLOVER_DOMAINS, (r) => r.rows);

/** The generic query endpoint: one page, exactly as asked for. */
export const reportingQuery = defineQuery({
  namespace: 'reporting',
  name: 'reporting.query',
  domains: REPORTING_DOMAINS,
  request: (request: ReportQueryRequest, signal) =>
    api.post<ReportQueryResponse>('/reporting/query', { body: request, signal }),
});

/** Authoritative campaign metadata + reporting + pacing projection. */
export const campaignOverview = defineQuery({
  namespace: 'reporting',
  name: 'campaigns.overview',
  domains: CAMPAIGN_OVERVIEW_DOMAINS,
  request: (request: CampaignOverviewRequest, signal: AbortSignal) =>
    api.post<CampaignOverviewResponse>('/campaigns/overview', { body: request, signal }),
  keepPreviousData: true,
});

/**
 * The same endpoint, walked to exhaustion.
 *
 * **Its name changed, and that is the point.** The key used to be
 * `[...reporting.query(request), 'all-pages']`, which sits UNDERNEATH
 * `reportingQuery`'s prefix — so `invalidateQueries({ queryKey: reporting.query
 * })` also invalidated this twenty-page walk, and nothing said so at either
 * site. As its own name the two are siblings, and the registry's
 * "no prefix nests inside another" test keeps them that way.
 *
 * The page bound is a real ceiling, not a guess: exceeding it throws rather
 * than silently returning a truncated result set that would read as a complete
 * report.
 */
export const reportingQueryAllPages = defineQuery({
  namespace: 'reporting',
  name: 'reporting.queryAllPages',
  domains: REPORTING_DOMAINS,
  request: async (request: ReportQueryRequest, signal): Promise<ReportQueryResponse> => {
    const walk = async (): Promise<ReportQueryResponse> => {
      const rows: ReportQueryResponse['rows'] = [];
      let cursor: string | null = null;
      for (let page = 0; page < 20; page += 1) {
        const response: ReportQueryResponse = await api.post<ReportQueryResponse>(
          '/reporting/query',
          { body: { ...request, cursor }, signal },
        );
        rows.push(...response.rows);
        cursor = response.next_cursor ?? null;
        if (!cursor) return { ...response, rows, next_cursor: null };
      }
      throw new Error('Reporting pagination exceeded its bounded page limit');
    };

    try {
      return await walk();
    } catch (error) {
      // A publication can advance between pages. The cursor is revision-bound,
      // so the API returns 409 instead of mixing generations. Restart exactly
      // once from page one; a second conflict is surfaced to the route.
      if (error instanceof ApiError && error.status === 409) return walk();
      throw error;
    }
  },
});
